import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';

const SectionCard = ({ title, description, children, footer }) => (
  <section className="bg-blanco-puro bg-opacity-10 rounded-xl p-6 shadow-lg shadow-azul-oscuro/20 border border-blanco-puro/10">
    <header className="mb-4">
      <h2 className="text-2xl font-bold text-amarillo-dorado">{title}</h2>
      {description && <p className="mt-2 text-sm text-blanco-puro/80">{description}</p>}
    </header>
    <div className="space-y-4">{children}</div>
    {footer && <div className="mt-6 pt-4 border-t border-blanco-puro/10">{footer}</div>}
  </section>
);

const TabButton = ({ id, label, description, isActive, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={isActive}
    aria-controls={`panel-${id}`}
    id={`tab-${id}`}
    onClick={() => onClick(id)}
    className={`flex flex-col text-left px-5 py-3 rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amarillo-dorado/80 min-w-[180px]
      ${
        isActive
          ? 'bg-amarillo-dorado text-azul-oscuro shadow-md shadow-amarillo-dorado/30'
          : 'bg-blanco-puro/10 text-blanco-puro/80 hover:bg-blanco-puro/20'
      }
    `}
  >
    <span className="font-semibold text-lg">{label}</span>
    {description && <span className="text-xs mt-1 opacity-80">{description}</span>}
  </button>
);

const Configuracion = () => {
  const [valorEntrada, setValorEntrada] = useState(0);
  const [guarderiaDiasGracia, setGuarderiaDiasGracia] = useState('0');
  const [guarderiaTasaMora, setGuarderiaTasaMora] = useState('0');
  const [guarderiaTipos, setGuarderiaTipos] = useState([]);
  const [nuevoTipoGuarderia, setNuevoTipoGuarderia] = useState('');
  const [historial, setHistorial] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [nuevoUsuario, setNuevoUsuario] = useState({ nombre: '', password: '', rol: 'vendedor', activo: true });
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [extras, setExtras] = useState([]);
  const [nuevoExtra, setNuevoExtra] = useState({ nombre: '', valor: '', descripcion: '', activo: true });
  const [extraEditando, setExtraEditando] = useState(null);
  const [modalExtraAbierto, setModalExtraAbierto] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const historialOrdenado = useMemo(() => {
    if (!Array.isArray(historial)) {
      return [];
    }
    return [...historial].sort(
      (a, b) => new Date(b.fechaActualizacion).getTime() - new Date(a.fechaActualizacion).getTime()
    );
  }, [historial]);

  const historialGeneral = useMemo(
    () =>
      historialOrdenado.filter(
        (item) => item.valorEntrada !== null && item.valorEntrada !== undefined
      ),
    [historialOrdenado]
  );

  const historialGuarderia = useMemo(() => {
    const tieneDato = (valor) => valor !== null && valor !== undefined;

    return historialOrdenado.filter((item) => {
      if (tieneDato(item.guarderiaDiasGracia) || tieneDato(item.guarderiaTasaMora)) {
        return true;
      }

      if (!tieneDato(item.guarderiaTipos)) {
        return false;
      }

      if (Array.isArray(item.guarderiaTipos)) {
        return item.guarderiaTipos.length > 0;
      }

      if (typeof item.guarderiaTipos === 'string') {
        return item.guarderiaTipos.trim().length > 0;
      }

      return true;
    });
  }, [historialOrdenado]);

  const parseTiposGuarderia = (tipos) => {
    if (!tipos) {
      return '-';
    }

    if (Array.isArray(tipos)) {
      return tipos.length ? tipos.join(', ') : '-';
    }

    if (typeof tipos === 'string') {
      try {
        const parsed = JSON.parse(tipos);
        if (Array.isArray(parsed)) {
          return parsed.length ? parsed.join(', ') : '-';
        }
        if (typeof parsed === 'string') {
          return parsed;
        }
        return String(parsed);
      } catch (error) {
        return tipos;
      }
    }

    return String(tipos);
  };

  const formatFecha = (fecha) => {
    if (!fecha) {
      return '-';
    }
    const date = new Date(fecha);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    return date.toLocaleString();
  };

  const tabs = [
    {
      id: 'general',
      label: 'General',
      description: 'Valor de entrada y contexto general',
    },
    {
      id: 'guarderia',
      label: 'Guardería',
      description: 'Períodos de gracia, tasas y tipos',
    },
    {
      id: 'extras',
      label: 'Extras',
      description: 'Servicios adicionales disponibles',
    },
    {
      id: 'usuarios',
      label: 'Usuarios',
      description: 'Gestión de accesos al sistema',
    },
  ];

  const fetchExtras = useCallback(async () => {
    try {
      const response = await api.get('/extras');
      setExtras(response.data);
    } catch (error) {
      console.error('Error al obtener los extras:', error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configRes, historialRes, usuariosRes, auditRes] = await Promise.all([
          api.get('/configuracion'),
          api.get('/configuracion/historial'),
          api.get('/usuarios'),
          api.get('/usuarios/audit-logs')
        ]);

        const configuracionData = configRes.data || {};
        setValorEntrada(configuracionData.valorEntrada ?? 0);
        setGuarderiaDiasGracia(
          configuracionData.guarderiaDiasGracia !== null && configuracionData.guarderiaDiasGracia !== undefined
            ? String(configuracionData.guarderiaDiasGracia)
            : '0'
        );
        setGuarderiaTasaMora(
          configuracionData.guarderiaTasaMora !== null && configuracionData.guarderiaTasaMora !== undefined
            ? String(configuracionData.guarderiaTasaMora)
            : '0'
        );
        setGuarderiaTipos(Array.isArray(configuracionData.guarderiaTipos) ? configuracionData.guarderiaTipos : []);
        setHistorial(historialRes.data);
        setUsuarios(usuariosRes.data);
        setAuditLogs(auditRes.data || []);

        await fetchExtras();
      } catch (error) {
        console.error('Error al obtener datos:', error);
      }
    };

    fetchData();
  }, [fetchExtras]);

  const handleGuardarConfiguracion = async () => {
    try {
      await api.post('/configuracion', {
        valorEntrada,
        guarderiaDiasGracia: Math.max(0, Number(guarderiaDiasGracia || 0)),
        guarderiaTasaMora: Math.max(0, Number(guarderiaTasaMora || 0)),
        guarderiaTipos,
      });
      const [configResponse, historialResponse] = await Promise.all([
        api.get('/configuracion'),
        api.get('/configuracion/historial'),
      ]);
      const configuracionActualizada = configResponse.data || {};
      setGuarderiaDiasGracia(
        configuracionActualizada.guarderiaDiasGracia !== null && configuracionActualizada.guarderiaDiasGracia !== undefined
          ? String(configuracionActualizada.guarderiaDiasGracia)
          : '0'
      );
      setGuarderiaTasaMora(
        configuracionActualizada.guarderiaTasaMora !== null && configuracionActualizada.guarderiaTasaMora !== undefined
          ? String(configuracionActualizada.guarderiaTasaMora)
          : '0'
      );
      setGuarderiaTipos(
        Array.isArray(configuracionActualizada.guarderiaTipos) ? configuracionActualizada.guarderiaTipos : []
      );
      setHistorial(historialResponse.data);
    } catch (error) {
      console.error('Error al guardar la configuración:', error);
    }
  };

  const handleAgregarTipoGuarderia = () => {
    const nombre = nuevoTipoGuarderia.trim();
    if (!nombre) {
      alert('El nombre del tipo de guardería es obligatorio.');
      return;
    }
    if (guarderiaTipos.some((tipo) => tipo.toLowerCase() === nombre.toLowerCase())) {
      alert('Ese tipo de guardería ya existe.');
      return;
    }
    setGuarderiaTipos((prev) => [...prev, nombre]);
    setNuevoTipoGuarderia('');
  };

  const handleEliminarTipoGuarderia = (tipo) => {
    setGuarderiaTipos((prev) => prev.filter((item) => item !== tipo));
  };

  const handleCrearUsuario = async () => {
    try {
      await api.post('/usuarios', nuevoUsuario);
      const usuariosResponse = await api.get('/usuarios');
      setUsuarios(usuariosResponse.data);
      setNuevoUsuario({ nombre: '', password: '', rol: 'vendedor', activo: true });
    } catch (error) {
      console.error('Error al crear el usuario:', error);
    }
  };

  const handleEditarUsuario = (usuario) => {
    setUsuarioEditando({ ...usuario, rol: usuario.rol || 'vendedor', activo: Boolean(usuario.activo) });
    setModalAbierto(true);
  };

  const handleActualizarUsuario = async () => {
    try {
      await api.put(`/usuarios/${usuarioEditando.id}`, usuarioEditando);
      const usuariosResponse = await api.get('/usuarios');
      setUsuarios(usuariosResponse.data);
      setModalAbierto(false);
    } catch (error) {
      console.error('Error al actualizar el usuario:', error);
    }
  };

  const handleEliminarUsuario = async (id) => {
    try {
      await api.delete(`/usuarios/${id}`);
      setUsuarios(usuarios.filter(u => u.id !== id));
    } catch (error) {
      console.error('Error al eliminar el usuario:', error);
    }
  };

  const limpiarNuevoExtra = () => {
    setNuevoExtra({ nombre: '', valor: '', descripcion: '', activo: true });
  };

  const handleCrearExtra = async () => {
    const nombre = nuevoExtra.nombre.trim();
    const valorNumero = Number(nuevoExtra.valor);

    if (!nombre) {
      alert('El nombre del extra es obligatorio.');
      return;
    }

    if (!Number.isFinite(valorNumero) || valorNumero < 0) {
      alert('El valor del extra debe ser un número mayor o igual a 0.');
      return;
    }

    try {
      await api.post('/extras', {
        nombre,
        valor: Number(valorNumero.toFixed(2)),
        descripcion: nuevoExtra.descripcion?.trim() || null,
        activo: Boolean(nuevoExtra.activo),
      });

      await fetchExtras();
      limpiarNuevoExtra();
    } catch (error) {
      console.error('Error al crear el extra:', error);
      alert('No se pudo crear el extra. Por favor, intente nuevamente.');
    }
  };

  const handleEditarExtra = (extra) => {
    setExtraEditando({
      ...extra,
      valor: Number(extra.valor),
    });
    setModalExtraAbierto(true);
  };

  const handleActualizarExtra = async () => {
    if (!extraEditando) return;

    const nombre = extraEditando.nombre?.trim();
    const valorNumero = Number(extraEditando.valor);

    if (!nombre) {
      alert('El nombre del extra es obligatorio.');
      return;
    }

    if (!Number.isFinite(valorNumero) || valorNumero < 0) {
      alert('El valor del extra debe ser un número mayor o igual a 0.');
      return;
    }

    try {
      await api.put(`/extras/${extraEditando.id}`, {
        nombre,
        descripcion: extraEditando.descripcion?.trim() || null,
        valor: Number(valorNumero.toFixed(2)),
        activo: Boolean(extraEditando.activo),
      });

      await fetchExtras();
      setModalExtraAbierto(false);
      setExtraEditando(null);
    } catch (error) {
      console.error('Error al actualizar el extra:', error);
      alert('No se pudo actualizar el extra.');
    }
  };

  const handleEliminarExtra = async (id) => {
    const confirmar = window.confirm('¿Estás seguro de eliminar este extra? Esta acción no se puede deshacer.');
    if (!confirmar) return;

    try {
      await api.delete(`/extras/${id}`);
      await fetchExtras();
    } catch (error) {
      console.error('Error al eliminar el extra:', error);
      alert('No se pudo eliminar el extra.');
    }
  };

  const handleToggleActivoExtra = async (extra) => {
    try {
      await api.put(`/extras/${extra.id}`, {
        activo: !extra.activo,
      });
      await fetchExtras();
    } catch (error) {
      console.error('Error al actualizar el estado del extra:', error);
      alert('No se pudo actualizar el estado del extra.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-azul-oscuro to-azul-lago text-blanco-puro p-4 md:p-8">
      <header className="mb-10 space-y-4">
        <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-blanco-puro/60 bg-blanco-puro/10 px-3 py-1 rounded-full">
          Panel Administrativo
        </p>
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-amarillo-dorado">Configuración del Sistema</h1>
          <p className="mt-3 text-base md:text-lg text-blanco-puro/80 max-w-3xl">
            Organiza los parámetros clave del sistema desde un mismo lugar. Utiliza las categorías inferiores para encontrar rápidamente lo que necesitas modificar.
          </p>
        </div>
      </header>

      <nav
        className="mb-8 flex flex-wrap gap-3"
        role="tablist"
        aria-label="Secciones de configuración"
      >
        {tabs.map((tab) => (
          <TabButton
            key={tab.id}
            id={tab.id}
            label={tab.label}
            description={tab.description}
            isActive={activeTab === tab.id}
            onClick={setActiveTab}
          />
        ))}
      </nav>

      <div className="space-y-10">
        {activeTab === 'general' && (
          <div
            role="tabpanel"
            id="panel-general"
            aria-labelledby="tab-general"
            className="transition-opacity duration-200"
          >
            <SectionCard
              title="Valor de Entrada"
              description="Actualiza el precio que se utilizará como referencia principal en los procesos del sistema."
              footer={
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <p className="text-sm text-blanco-puro/70">
                    Última actualización automática cuando se guarda cualquier ajuste general.
                  </p>
                  <button
                    onClick={handleGuardarConfiguracion}
                    className="px-6 py-2 rounded-md bg-amarillo-dorado hover:bg-amber-600 text-azul-oscuro font-semibold transition-colors"
                  >
                    Guardar cambios generales
                  </button>
                </div>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Precio actual</label>
                  <input
                    type="number"
                    value={valorEntrada}
                    onChange={(e) => setValorEntrada(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                  />
                </div>
                <div className="self-end">
                  <div className="rounded-lg bg-blanco-puro/10 p-4 text-sm text-blanco-puro/80">
                    <p className="font-semibold text-amarillo-dorado uppercase tracking-wide text-xs mb-1">Sugerencia</p>
                    <p>
                      Si realizas cambios frecuentes, considera registrar el motivo en el historial para mantener al equipo informado.
                    </p>
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Historial de valor de entrada"
              description="Consulta los ajustes registrados para el valor de entrada."
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-blanco-puro/10 text-left text-sm uppercase tracking-wide text-blanco-puro/60">
                      <th className="py-3 px-4">Valor de entrada</th>
                      <th className="py-3 px-4">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historialGeneral.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="py-5 px-4 text-center text-sm text-blanco-puro/60">
                          Aún no se registran cambios.
                        </td>
                      </tr>
                    ) : (
                      historialGeneral.map((item, index) => (
                        <tr
                          key={`${item.fechaActualizacion}-${index}`}
                          className="border-b border-blanco-puro/5 hover:bg-blanco-puro/10 transition"
                        >
                          <td className="py-3 px-4">${item.valorEntrada}</td>
                          <td className="py-3 px-4">{formatFecha(item.fechaActualizacion)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

          </div>
        )}

        {activeTab === 'guarderia' && (
          <div
            role="tabpanel"
            id="panel-guarderia"
            aria-labelledby="tab-guarderia"
            className="space-y-8 transition-opacity duration-200"
          >
            <SectionCard
              title="Parámetros de Guardería"
              description="Configura los aspectos financieros de la guardería para reflejar tus políticas internas."
              footer={
                <div className="flex justify-end">
                  <button
                    onClick={handleGuardarConfiguracion}
                    className="px-6 py-2 rounded-md bg-amarillo-dorado hover:bg-amber-600 text-azul-oscuro font-semibold transition-colors"
                  >
                    Guardar configuración de guardería
                  </button>
                </div>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Período de gracia (días)</label>
                  <input
                    type="number"
                    min="0"
                    value={guarderiaDiasGracia}
                    onChange={(e) => setGuarderiaDiasGracia(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Tasa de mora (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={guarderiaTasaMora}
                    onChange={(e) => setGuarderiaTasaMora(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                  />
                </div>
                <div>
                  <div className="rounded-lg bg-azul-oscuro/40 p-4 h-full flex flex-col justify-center">
                    <p className="text-sm text-blanco-puro/70">
                      Utiliza valores conservadores para evitar sorpresas en el cálculo de recargos.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Tipos de guardería</label>
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                  <input
                    type="text"
                    value={nuevoTipoGuarderia}
                    onChange={(e) => setNuevoTipoGuarderia(e.target.value)}
                    className="w-full md:w-1/2 px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                    placeholder="Ej: Guardería 1, Guardería premium"
                  />
                  <button
                    onClick={handleAgregarTipoGuarderia}
                    className="px-5 py-2 rounded-md bg-azul-cielo hover:bg-blue-600 text-blanco-puro font-medium transition-colors"
                    type="button"
                  >
                    Agregar tipo
                  </button>
                </div>

                {guarderiaTipos.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {guarderiaTipos.map((tipo) => (
                      <span
                        key={tipo}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-azul-lago/70 text-blanco-puro text-sm"
                      >
                        {tipo}
                        <button
                          type="button"
                          onClick={() => handleEliminarTipoGuarderia(tipo)}
                          aria-label={`Eliminar ${tipo}`}
                          className="hover:text-amarillo-dorado transition"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-blanco-puro/60">Aún no se configuraron tipos personalizados.</p>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Historial de parámetros de guardería"
              description="Revisa los valores aplicados previamente para los parámetros de guardería."
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-blanco-puro/10 text-left text-sm uppercase tracking-wide text-blanco-puro/60">
                      <th className="py-3 px-4">Período de gracia (días)</th>
                      <th className="py-3 px-4">Tasa de mora (%)</th>
                      <th className="py-3 px-4">Tipos configurados</th>
                      <th className="py-3 px-4">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historialGuarderia.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-5 px-4 text-center text-sm text-blanco-puro/60">
                          Aún no se registran cambios.
                        </td>
                      </tr>
                    ) : (
                      historialGuarderia.map((item, index) => {
                        const dias =
                          item.guarderiaDiasGracia !== null && item.guarderiaDiasGracia !== undefined
                            ? Number(item.guarderiaDiasGracia)
                            : '-';
                        const tasa =
                          item.guarderiaTasaMora !== null && item.guarderiaTasaMora !== undefined
                            ? Number(item.guarderiaTasaMora).toFixed(2)
                            : '-';

                        return (
                          <tr
                            key={`guarderia-${item.fechaActualizacion}-${index}`}
                            className="border-b border-blanco-puro/5 hover:bg-blanco-puro/10 transition"
                          >
                            <td className="py-3 px-4">{dias}</td>
                            <td className="py-3 px-4">{tasa}</td>
                            <td className="py-3 px-4">{parseTiposGuarderia(item.guarderiaTipos)}</td>
                            <td className="py-3 px-4">{formatFecha(item.fechaActualizacion)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        )}

        {activeTab === 'extras' && (
          <div
            role="tabpanel"
            id="panel-extras"
            aria-labelledby="tab-extras"
            className="space-y-8 transition-opacity duration-200"
          >
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <SectionCard
                title="Crear Extra"
                description="Añade servicios adicionales para ofrecer a tus clientes más opciones."
                footer={
                  <div className="flex flex-wrap gap-3 justify-end">
                    <button
                      onClick={limpiarNuevoExtra}
                      className="px-5 py-2 rounded-md bg-blanco-puro/10 hover:bg-blanco-puro/20 text-blanco-puro transition"
                    >
                      Limpiar
                    </button>
                    <button
                      onClick={handleCrearExtra}
                      className="px-6 py-2 rounded-md bg-verde-esmeralda hover:bg-emerald-600 text-blanco-puro font-semibold transition-colors"
                    >
                      Guardar Extra
                    </button>
                  </div>
                }
              >
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Nombre</label>
                    <input
                      type="text"
                      value={nuevoExtra.nombre}
                      onChange={(e) => setNuevoExtra({ ...nuevoExtra, nombre: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Valor</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={nuevoExtra.valor}
                        onChange={(e) => setNuevoExtra({ ...nuevoExtra, valor: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        id="nuevo-extra-activo"
                        type="checkbox"
                        checked={Boolean(nuevoExtra.activo)}
                        onChange={(e) => setNuevoExtra({ ...nuevoExtra, activo: e.target.checked })}
                        className="h-4 w-4"
                      />
                      <label htmlFor="nuevo-extra-activo" className="text-sm text-blanco-puro/80">
                        Extra disponible inmediatamente
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Descripción (opcional)</label>
                    <textarea
                      value={nuevoExtra.descripcion}
                      onChange={(e) => setNuevoExtra({ ...nuevoExtra, descripcion: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                      rows={4}
                    />
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title="Extras Disponibles"
                description="Consulta y administra los extras activos o inactivos."
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-blanco-puro/10 text-left text-sm uppercase tracking-wide text-blanco-puro/60">
                        <th className="py-3 px-4">Nombre</th>
                        <th className="py-3 px-4">Valor</th>
                        <th className="py-3 px-4">Estado</th>
                        <th className="py-3 px-4">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extras.length === 0 && (
                        <tr>
                          <td colSpan={4} className="py-5 px-4 text-center text-sm text-blanco-puro/60">
                            No hay extras configurados todavía.
                          </td>
                        </tr>
                      )}
                      {extras.map((extra) => (
                        <tr
                          key={extra.id}
                          className="border-b border-blanco-puro/5 hover:bg-blanco-puro/10 transition"
                        >
                          <td className="py-3 px-4 align-top">
                            <div className="font-semibold text-base">{extra.nombre}</div>
                            {extra.descripcion && (
                              <div className="text-sm text-blanco-puro/70 mt-1">{extra.descripcion}</div>
                            )}
                          </td>
                          <td className="py-3 px-4 align-top">${Number(extra.valor || 0).toFixed(2)}</td>
                          <td className="py-3 px-4 align-top">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                extra.activo
                                  ? 'bg-verde-esmeralda bg-opacity-20 text-verde-esmeralda'
                                  : 'bg-rojo-pasion bg-opacity-20 text-rojo-pasion'
                              }`}
                            >
                              {extra.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="py-3 px-4 space-x-2 whitespace-nowrap">
                            <button
                              onClick={() => handleEditarExtra(extra)}
                              className="px-3 py-1 rounded-md bg-azul-cielo hover:bg-blue-600 text-blanco-puro text-xs uppercase tracking-wide"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleToggleActivoExtra(extra)}
                              className="px-3 py-1 rounded-md bg-amarillo-dorado hover:bg-amber-500 text-azul-oscuro text-xs uppercase tracking-wide"
                            >
                              {extra.activo ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              onClick={() => handleEliminarExtra(extra.id)}
                              className="px-3 py-1 rounded-md bg-rojo-pasion hover:bg-red-600 text-blanco-puro text-xs uppercase tracking-wide"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>

            <SectionCard
              title="Historial de extras"
              description="Registra los cambios relevantes realizados sobre los extras."
            >
              <p className="text-sm text-blanco-puro/70">
                Aún no se registran cambios en los extras.
              </p>
            </SectionCard>
          </div>
        )}

        {activeTab === 'usuarios' && (
          <div
            role="tabpanel"
            id="panel-usuarios"
            aria-labelledby="tab-usuarios"
            className="space-y-8 transition-opacity duration-200"
          >
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              <SectionCard
                title="Crear Nuevo Usuario"
                description="Otorga acceso al sistema asignando credenciales temporales o definitivas."
                footer={
                  <div className="flex justify-end">
                    <button
                      onClick={handleCrearUsuario}
                      className="px-6 py-2 rounded-md bg-verde-esmeralda hover:bg-emerald-600 text-blanco-puro font-semibold transition-colors"
                    >
                      Crear Usuario
                    </button>
                  </div>
                }
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Nombre</label>
                    <input
                      type="text"
                      value={nuevoUsuario.nombre}
                      onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, nombre: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">PIN operativo</label>
                    <input
                      type="password"
                      value={nuevoUsuario.password}
                      onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, password: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg bg-blanco-puro/10 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm uppercase tracking-wide text-blanco-puro/60 mb-2">Rol</label>
                      <select
                        value={nuevoUsuario.rol}
                        onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, rol: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg bg-azul-oscuro/40 text-blanco-puro border border-transparent focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/60 transition"
                      >
                        <option value="vendedor">Vendedor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="inline-flex items-center gap-2 text-sm text-blanco-puro/80">
                        <input
                          type="checkbox"
                          checked={Boolean(nuevoUsuario.activo)}
                          onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, activo: e.target.checked })}
                          className="h-4 w-4"
                        />
                        Usuario activo
                      </label>
                    </div>
                  </div>
                  <div className="rounded-lg bg-blanco-puro/10 p-4 text-sm text-blanco-puro/70">
                    <p>
                      Crea contraseñas robustas combinando letras, números y símbolos. Comunica el acceso por un canal seguro.
                    </p>
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title="Usuarios Registrados"
                description="Consulta quiénes tienen acceso y gestiona sus permisos en segundos."
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-blanco-puro/10 text-left text-sm uppercase tracking-wide text-blanco-puro/60">
                        <th className="py-3 px-4">ID</th>
                        <th className="py-3 px-4">Nombre</th>
                        <th className="py-3 px-4">Rol</th>
                        <th className="py-3 px-4">Activo</th>
                        <th className="py-3 px-4">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuarios.map((usuario) => (
                        <tr
                          key={usuario.id}
                          className="border-b border-blanco-puro/5 hover:bg-blanco-puro/10 transition"
                        >
                          <td className="py-3 px-4">{usuario.id}</td>
                          <td className="py-3 px-4">{usuario.nombre}</td>
                          <td className="py-3 px-4">{usuario.rol || 'vendedor'}</td>
                          <td className="py-3 px-4">{usuario.activo ? 'Sí' : 'No'}</td>
                          <td className="py-3 px-4 space-x-2">
                            <button
                              onClick={() => handleEditarUsuario(usuario)}
                              className="px-3 py-1 rounded-md bg-azul-cielo hover:bg-blue-600 text-blanco-puro text-xs uppercase tracking-wide"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleEliminarUsuario(usuario.id)}
                              className="px-3 py-1 rounded-md bg-rojo-pasion hover:bg-red-600 text-blanco-puro text-xs uppercase tracking-wide"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>

            <SectionCard
              title="Historial de usuarios"
              description="Auditoría real de acciones sensibles y cambios sobre usuarios."
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-blanco-puro/10 text-left text-sm uppercase tracking-wide text-blanco-puro/60">
                      <th className="py-3 px-4">Fecha</th>
                      <th className="py-3 px-4">Acción</th>
                      <th className="py-3 px-4">Entidad</th>
                      <th className="py-3 px-4">Actor</th>
                      <th className="py-3 px-4">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-5 px-4 text-center text-sm text-blanco-puro/60">
                          Aún no se registran eventos de auditoría.
                        </td>
                      </tr>
                    ) : (
                      auditLogs.map((log) => (
                        <tr key={log.id} className="border-b border-blanco-puro/5 hover:bg-blanco-puro/10 transition">
                          <td className="py-3 px-4">{formatFecha(log.createdAt)}</td>
                          <td className="py-3 px-4">{log.accion}</td>
                          <td className="py-3 px-4">{log.entidad}{log.entidad_id ? ` #${log.entidad_id}` : ''}</td>
                          <td className="py-3 px-4">{log.actor?.nombre || '-'}</td>
                          <td className="py-3 px-4">{log.descripcion || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            <SectionCard
              title="Matriz de permisos por rol"
              description="Resumen operativo de accesos definidos en esta etapa incremental."
            >
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div className="rounded-lg bg-blanco-puro/10 p-4">
                  <h4 className="font-semibold text-amarillo-dorado mb-2">Admin</h4>
                  <ul className="list-disc pl-5 space-y-1 text-blanco-puro/80">
                    <li>Acceso a Dashboard</li>
                    <li>Acceso a Históricos</li>
                    <li>Acceso a Configuración</li>
                    <li>Autorizar anulaciones por PIN</li>
                    <li>Gestionar usuarios (crear/editar/eliminar)</li>
                  </ul>
                </div>
                <div className="rounded-lg bg-blanco-puro/10 p-4">
                  <h4 className="font-semibold text-amarillo-dorado mb-2">Vendedor</h4>
                  <ul className="list-disc pl-5 space-y-1 text-blanco-puro/80">
                    <li>Registrar ventas con PIN operativo</li>
                    <li>Sin acceso a Dashboard</li>
                    <li>Sin acceso a Históricos</li>
                    <li>Sin acceso a Configuración</li>
                    <li>No puede autorizar anulaciones</li>
                  </ul>
                </div>
              </div>
            </SectionCard>
          </div>
        )}
      </div>

      {/* Modal de Edición de Extras */}
      {modalExtraAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-blanco-puro bg-opacity-20 backdrop-blur-md rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-amarillo-dorado mb-4">Editar Extra</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-lg mb-2">Nombre:</label>
                <input
                  type="text"
                  value={extraEditando?.nombre || ''}
                  onChange={(e) => setExtraEditando({ ...extraEditando, nombre: e.target.value })}
                  className="w-full px-4 py-2 rounded-md bg-blanco-puro bg-opacity-20 text-blanco-puro border border-gris-piedra"
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Valor:</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={extraEditando?.valor ?? ''}
                  onChange={(e) => setExtraEditando({ ...extraEditando, valor: e.target.value })}
                  className="w-full px-4 py-2 rounded-md bg-blanco-puro bg-opacity-20 text-blanco-puro border border-gris-piedra"
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Descripción:</label>
                <textarea
                  value={extraEditando?.descripcion ?? ''}
                  onChange={(e) => setExtraEditando({ ...extraEditando, descripcion: e.target.value })}
                  className="w-full px-4 py-2 rounded-md bg-blanco-puro bg-opacity-20 text-blanco-puro border border-gris-piedra"
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="editar-extra-activo"
                  type="checkbox"
                  checked={Boolean(extraEditando?.activo)}
                  onChange={(e) => setExtraEditando({ ...extraEditando, activo: e.target.checked })}
                  className="h-4 w-4"
                />
                <label htmlFor="editar-extra-activo" className="text-lg">Activo</label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setModalExtraAbierto(false);
                    setExtraEditando(null);
                  }}
                  className="px-4 py-2 rounded-md bg-gris-piedra hover:bg-gray-600 text-blanco-puro"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleActualizarExtra}
                  className="px-4 py-2 rounded-md bg-verde-esmeralda hover:bg-emerald-600 text-blanco-puro"
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {modalAbierto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-blanco-puro bg-opacity-20 backdrop-blur-md rounded-lg p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-amarillo-dorado mb-4">Editar Usuario</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-lg mb-2">Nombre:</label>
                <input
                  type="text"
                  value={usuarioEditando?.nombre || ''}
                  onChange={(e) => setUsuarioEditando({...usuarioEditando, nombre: e.target.value})}
                  className="w-full px-4 py-2 rounded-md bg-blanco-puro bg-opacity-20 text-blanco-puro border border-gris-piedra"
                />
              </div>
              <div>
                <label className="block text-lg mb-2">PIN operativo:</label>
                <input
                  type="password"
                  value={usuarioEditando?.password || ''}
                  onChange={(e) => setUsuarioEditando({...usuarioEditando, password: e.target.value})}
                  className="w-full px-4 py-2 rounded-md bg-blanco-puro bg-opacity-20 text-blanco-puro border border-gris-piedra"
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Rol:</label>
                <select
                  value={usuarioEditando?.rol || 'vendedor'}
                  onChange={(e) => setUsuarioEditando({ ...usuarioEditando, rol: e.target.value })}
                  className="w-full px-4 py-2 rounded-md bg-blanco-puro bg-opacity-20 text-blanco-puro border border-gris-piedra"
                >
                  <option value="vendedor">Vendedor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="editar-usuario-activo"
                  type="checkbox"
                  checked={Boolean(usuarioEditando?.activo)}
                  onChange={(e) => setUsuarioEditando({ ...usuarioEditando, activo: e.target.checked })}
                  className="h-4 w-4"
                />
                <label htmlFor="editar-usuario-activo" className="text-lg">Activo</label>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setModalAbierto(false)}
                  className="px-4 py-2 rounded-md bg-gris-piedra hover:bg-gray-600 text-blanco-puro"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleActualizarUsuario}
                  className="px-4 py-2 rounded-md bg-verde-esmeralda hover:bg-emerald-600 text-blanco-puro"
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configuracion;